import { Button, confirm, Input, Modal, toast } from "@devhop/ui";
import { Link, useParams } from "@tanstack/react-router";
import {
  Fragment,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { toDateInputValue } from "@/shared/lib/date-input";
import { formatConfigurationPeriodRange } from "@/shared/lib/vehicle-configuration-period";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import { AppImage, resolveImageSrc } from "@/shared/ui/AppImage";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { RowActions } from "@/shared/ui/RowActions";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

import type {
  EpcDiagramPartDTO,
  EpcGroupDTO,
  VehicleChassisRangeDTO,
} from "../api/client";
import {
  useChassisRangesQuery,
  useCreateEpcDiagram,
  useCreateEpcDiagramPart,
  useCreateEpcGroup,
  useCreateVehicleChassisRange,
  useDeleteEpcDiagram,
  useDeleteEpcDiagramPart,
  useDeleteEpcGroup,
  useDeleteVehicleChassisRange,
  useEpcDiagramPartsQuery,
  useEpcDiagramsQuery,
  useEpcGroupsQuery,
  useUpdateEpcDiagram,
  useUpdateEpcDiagramPart,
  useUpdateEpcGroup,
  useUpdateVehicleChassisRange,
  useVehicleConfigurationDetailQuery,
} from "../api/queries";
import { VehicleModelCodesSection } from "../ui/VehicleModelCodesSection";
import { VehicleConfigurationCodeAssignmentsSection } from "../ui/VehicleConfigurationCodeAssignmentsSection";
import { EpcCalloutProductSkuPicker } from "../ui/EpcCalloutProductSkuPicker";

const EPC_DIAGRAM_IMAGE_KEY_PREFIX = "uploads/epc-diagrams";

function epcGroupDepth(groups: EpcGroupDTO[], id: string): number {
  const map = new Map(groups.map((g) => [g.id, g]));
  let d = 0;
  let cur: string | null | undefined = id;
  const seen = new Set<string>();
  while (cur) {
    if (seen.has(cur)) break;
    seen.add(cur);
    const g = map.get(cur);
    if (!g?.parentGroupId) break;
    cur = g.parentGroupId;
    d += 1;
    if (d > 50) break;
  }
  return d;
}

function clampPct(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/** ຈົນກອ້ງ ຈຸດ ກົງ/ກຳນົດ (ກຳນົດກຳນົດ) ສຽງ ກອ້ງ x,y ກົງ ສຽງ ກົງ ສຽງ ກົງ ສຽງ box */
function clampCenterPct(
  cx: number,
  cy: number,
  w: number | null,
  h: number | null,
): { cx: number; cy: number } {
  if (
    w != null &&
    h != null &&
    Number.isFinite(w) &&
    Number.isFinite(h) &&
    w > 0 &&
    h > 0
  ) {
    return {
      cx: clampPct(cx, w / 2, 100 - w / 2),
      cy: clampPct(cy, h / 2, 100 - h / 2),
    };
  }
  return { cx: clampPct(cx, 0, 100), cy: clampPct(cy, 0, 100) };
}

function round2Pct(n: number): number {
  return Number(n.toFixed(2));
}

function pctFromClientPoint(
  clientX: number,
  clientY: number,
  rect: DOMRect,
): { xPct: number; yPct: number } {
  const w = Math.max(rect.width, 1e-6);
  const h = Math.max(rect.height, 1e-6);
  return {
    xPct: round2Pct(((clientX - rect.left) / w) * 100),
    yPct: round2Pct(((clientY - rect.top) / h) * 100),
  };
}

function boxFromTwoPoints(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): { cx: number; cy: number; wPct: number; hPct: number } {
  const MIN_SIDE = 1;
  const left = Math.min(x1, x2);
  const right = Math.max(x1, x2);
  const top = Math.min(y1, y2);
  const bottom = Math.max(y1, y2);
  const wPct = Math.max(MIN_SIDE, right - left);
  const hPct = Math.max(MIN_SIDE, bottom - top);
  return {
    cx: round2Pct((left + right) / 2),
    cy: round2Pct((top + bottom) / 2),
    wPct: round2Pct(wPct),
    hPct: round2Pct(hPct),
  };
}

type ResizeCorner = "nw" | "ne" | "sw" | "se";

/** ມຸມກົງກັນຂ້າມກັບ handle ທີ່ລາກ — ຄົງມຸມນັ້ນໄວ້ */
function fixedCornerForResize(
  cx: number,
  cy: number,
  w: number,
  h: number,
  handle: ResizeCorner,
): { xPct: number; yPct: number } {
  const left = cx - w / 2;
  const right = cx + w / 2;
  const top = cy - h / 2;
  const bottom = cy + h / 2;
  switch (handle) {
    case "se":
      return { xPct: left, yPct: top };
    case "nw":
      return { xPct: right, yPct: bottom };
    case "ne":
      return { xPct: left, yPct: bottom };
    case "sw":
      return { xPct: right, yPct: top };
  }
}

const RESIZE_HANDLE_CURSORS: Record<ResizeCorner, string> = {
  nw: "cursor-nwse-resize",
  se: "cursor-nwse-resize",
  ne: "cursor-nesw-resize",
  sw: "cursor-nesw-resize",
};

/** ກອ້ງ draft ກົງກຳນົດ form — ລາກໄດ້ເມື່ອ interactive */
function CalloutFormPreviewOverlay({
  cx,
  cy,
  wPct,
  hPct,
  label,
  interactive,
  onDragStart,
}: {
  cx: number;
  cy: number;
  wPct: number | null;
  hPct: number | null;
  label: string;
  interactive?: boolean;
  onDragStart?: (e: ReactPointerEvent) => void;
}) {
  const hasBox =
    wPct != null &&
    hPct != null &&
    Number.isFinite(wPct) &&
    Number.isFinite(hPct) &&
    wPct > 0 &&
    hPct > 0;

  const dragClass = interactive
    ? "pointer-events-auto cursor-grab touch-none active:cursor-grabbing"
    : "pointer-events-none";

  const badge = (
    <div
      data-draft-callout
      className={`absolute z-5 inline-flex min-h-6 min-w-6 max-w-[calc(100%-0.5rem)] -translate-x-1/2 -translate-y-1/2 items-center justify-center overflow-hidden rounded-full border-2 border-cyan-200 bg-cyan-600 px-2 py-0.5 font-bold text-[10px] text-white leading-none shadow ${dragClass}`}
      style={{
        left: `${cx}%`,
        top: `${cy}%`,
      }}
      title={interactive ? "ລາກເພື່ອຈັດຕຳແໜ່ງ" : undefined}
      onPointerDown={interactive ? onDragStart : undefined}
    >
      <span className="block min-w-0 max-w-full truncate text-center">
        {label || "⋯"}
      </span>
    </div>
  );

  if (!hasBox) {
    return (
      <div className="pointer-events-none absolute inset-0 z-4">{badge}</div>
    );
  }

  let left = cx - wPct / 2;
  let top = cy - hPct / 2;
  left = clampPct(left, 0, Math.max(0, 100 - wPct));
  top = clampPct(top, 0, Math.max(0, 100 - hPct));

  return (
    <div className="pointer-events-none absolute inset-0 z-4">
      <div
        data-draft-callout
        className={`absolute border-2 border-cyan-400 border-dashed bg-cyan-100/80 ${dragClass}`}
        style={{
          left: `${left}%`,
          top: `${top}%`,
          width: `${wPct}%`,
          height: `${hPct}%`,
        }}
        title={interactive ? "ລາກເພື່ອຈັດຕຳແໜ່ງ" : undefined}
        onPointerDown={interactive ? onDragStart : undefined}
      />
      {badge}
    </div>
  );
}

/** ກອ້ງ ສຽງ ກົງ ສຽງ ກົງ ສ້າງ · ກົງ draggable ກົງ canDragMove */
function EpcDiagramPartMarkers({
  parts,
  canDragMove,
  highlightPartId,
  onSavedPartDragStart,
  onSavedPartResizeStart,
}: {
  parts: EpcDiagramPartDTO[];
  canDragMove: boolean;
  highlightPartId?: string | null;
  onSavedPartDragStart: (part: EpcDiagramPartDTO, e: ReactPointerEvent) => void;
  onSavedPartResizeStart?: (
    part: EpcDiagramPartDTO,
    corner: ResizeCorner,
    e: ReactPointerEvent,
  ) => void;
}) {
  const dragInteract = canDragMove
    ? "pointer-events-auto cursor-grab touch-none active:cursor-grabbing"
    : "pointer-events-none";

  return (
    <>
      {parts.map((p) => {
        const cx = Number(p.xPct);
        const cy = Number(p.yPct);
        const w = p.widthPct != null ? Number(p.widthPct) : Number.NaN;
        const h = p.heightPct != null ? Number(p.heightPct) : Number.NaN;
        const highlighted = highlightPartId === p.id;
        const hasBox =
          Number.isFinite(cx) &&
          Number.isFinite(cy) &&
          Number.isFinite(w) &&
          Number.isFinite(h) &&
          w > 0 &&
          h > 0;

        const badge = (
          <div
            data-callout-marker
            className={`absolute z-3 inline-flex min-h-6 min-w-6 max-w-[calc(100%-0.5rem)] -translate-x-1/2 -translate-y-1/2 items-center justify-center overflow-hidden rounded-full border-2 px-2 py-0.5 font-bold text-[10px] text-white leading-none shadow ${highlighted ? "border-yellow-300 bg-yellow-500 ring-2 ring-yellow-400/80" : "border-white bg-red-600"} ${dragInteract}`}
            style={{
              left: `${cx}%`,
              top: `${cy}%`,
            }}
            title={
              canDragMove
                ? hasBox
                  ? `ລາກ ຍ້າຍ · ຈຸດຂອບ ປັບຂະໜາດ #${p.calloutNumber}`
                  : `ລາກ ຍ້າຍ #${p.calloutNumber}`
                : `#${p.calloutNumber}`
            }
            onPointerDown={(e) =>
              canDragMove ? onSavedPartDragStart(p, e) : undefined
            }
          >
            <span className="block min-w-0 max-w-full truncate text-center">
              {p.calloutNumber}
            </span>
          </div>
        );

        if (!hasBox) {
          return <Fragment key={p.id}>{badge}</Fragment>;
        }

        let left = cx - w / 2;
        let top = cy - h / 2;
        left = clampPct(left, 0, Math.max(0, 100 - w));
        top = clampPct(top, 0, Math.max(0, 100 - h));

        const resizeHandles: ResizeCorner[] = ["nw", "ne", "sw", "se"];
        const handlePos: Record<
          ResizeCorner,
          { left: string; top: string; cursor: string }
        > = {
          nw: {
            left: `${left}%`,
            top: `${top}%`,
            cursor: RESIZE_HANDLE_CURSORS.nw,
          },
          ne: {
            left: `${left + w}%`,
            top: `${top}%`,
            cursor: RESIZE_HANDLE_CURSORS.ne,
          },
          sw: {
            left: `${left}%`,
            top: `${top + h}%`,
            cursor: RESIZE_HANDLE_CURSORS.sw,
          },
          se: {
            left: `${left + w}%`,
            top: `${top + h}%`,
            cursor: RESIZE_HANDLE_CURSORS.se,
          },
        };

        return (
          <Fragment key={p.id}>
            <div
              data-callout-marker
              className={`absolute z-2 border-2 bg-red-100 ${highlighted ? "border-yellow-500 bg-yellow-100/70 ring-2 ring-yellow-400/60" : "border-red-600"} ${dragInteract}`}
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: `${w}%`,
                height: `${h}%`,
              }}
              onPointerDown={(e) =>
                canDragMove ? onSavedPartDragStart(p, e) : undefined
              }
            />
            {canDragMove && onSavedPartResizeStart
              ? resizeHandles.map((corner) => (
                  <button
                    key={`${p.id}-${corner}`}
                    type="button"
                    data-callout-resize
                    aria-label={`ປັບຂະໜາດເລກອ້າງອີງ #${p.calloutNumber} (${corner})`}
                    className={`absolute z-4 size-3 -translate-x-1/2 -translate-y-1/2 rounded-sm border-2 border-red-800 bg-white shadow touch-none hover:bg-red-50 ${handlePos[corner].cursor} ${highlighted ? "ring-1 ring-yellow-400" : ""}`}
                    style={{
                      left: handlePos[corner].left,
                      top: handlePos[corner].top,
                    }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onSavedPartResizeStart(p, corner, e);
                    }}
                  />
                ))
              : null}
            {badge}
          </Fragment>
        );
      })}
    </>
  );
}

type CalloutPlacementMode = "point" | "box";

export function CatalogVehicleTrimEPCPage() {
  const { makeId, modelId, configurationId } = useParams({
    from: "/app/catalog/vehicles/$makeId/models/$modelId/configurations/$configurationId/epc",
  });

  const canCreate = useActionPermission(["catalog:create"]);
  const canUpdate = useActionPermission(["catalog:update"]);
  const canDelete = useActionPermission(["catalog:delete"]);
  const canManage = !!(canCreate || canUpdate || canDelete);

  const configId = configurationId ?? "";
  const detailQuery = useVehicleConfigurationDetailQuery(configId);
  const epcGroupsQuery = useEpcGroupsQuery(configId);
  const chassisQuery = useChassisRangesQuery(configId);

  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedDiagramId, setSelectedDiagramId] = useState("");

  const diagramsQuery = useEpcDiagramsQuery(selectedGroupId);
  const partsQuery = useEpcDiagramPartsQuery(selectedDiagramId);

  const selectedDiagram =
    diagramsQuery.data?.find((d) => d.id === selectedDiagramId) ?? null;
  const selectedDiagramImageSrc = useMemo(
    () => resolveImageSrc(selectedDiagram?.imageUrl ?? null),
    [selectedDiagram?.imageUrl],
  );

  useEffect(() => {
    const list = diagramsQuery.data ?? [];
    if (!selectedDiagramId && list[0]?.id) setSelectedDiagramId(list[0].id);
    if (selectedDiagramId && !list.some((d) => d.id === selectedDiagramId)) {
      setSelectedDiagramId(list[0]?.id ?? "");
    }
  }, [diagramsQuery.data, selectedDiagramId]);

  useEffect(() => {
    const groups = epcGroupsQuery.data ?? [];
    if (!selectedGroupId && groups[0]?.id) setSelectedGroupId(groups[0].id);
    if (selectedGroupId && !groups.some((g) => g.id === selectedGroupId)) {
      setSelectedGroupId(groups[0]?.id ?? "");
    }
  }, [epcGroupsQuery.data, selectedGroupId]);

  const createChassis = useCreateVehicleChassisRange(configId);
  const updateChassis = useUpdateVehicleChassisRange(configId);
  const deleteChassis = useDeleteVehicleChassisRange(configId);

  const createGroup = useCreateEpcGroup(configId);
  const updateGroup = useUpdateEpcGroup(configId);
  const deleteGroup = useDeleteEpcGroup(configId);

  const createDiagram = useCreateEpcDiagram(selectedGroupId, configId);
  const deleteDiagram = useDeleteEpcDiagram(selectedGroupId, configId);
  const renameDiagram = useUpdateEpcDiagram(selectedGroupId, configId);

  const createPart = useCreateEpcDiagramPart(selectedDiagramId);
  const updatePart = useUpdateEpcDiagramPart(selectedDiagramId);
  const deletePart = useDeleteEpcDiagramPart(selectedDiagramId);

  const [groupOpen, setGroupOpen] = useState(false);
  const [gfName, setGfName] = useState("");
  const [gfCode, setGfCode] = useState("");
  const [gfSort, setGfSort] = useState("0");
  const [gfParent, setGfParent] = useState<string>("__root__");

  const [diagramOpen, setDiagramOpen] = useState(false);
  const [dfTitle, setDfTitle] = useState("");
  const [dfDiagramImageFile, setDfDiagramImageFile] = useState<File | null>(
    null,
  );
  const [dfSort, setDfSort] = useState("0");
  const [diagramModalSaving, setDiagramModalSaving] = useState(false);

  const [partCallout, setPartCallout] = useState("1");
  const [partProductId, setPartProductId] = useState("");
  const [partSkuIds, setPartSkuIds] = useState<string[]>([]);
  const [partX, setPartX] = useState("");
  const [partY, setPartY] = useState("");
  const [partW, setPartW] = useState("");
  const [partH, setPartH] = useState("");
  const [placementMode, setPlacementMode] =
    useState<CalloutPlacementMode>("box");
  const [boxDraw, setBoxDraw] = useState<{
    startXPct: number;
    startYPct: number;
    currentXPct: number;
    currentYPct: number;
  } | null>(null);
  const [placementHint, setPlacementHint] = useState("");
  const [editingPartId, setEditingPartId] = useState<string | null>(null);
  const [partQty, setPartQty] = useState("");
  const [partNote, setPartNote] = useState("");

  /** live preview ຕອນລາກ/resize callout ທີ່ບັນທຶກແລ້ວ */
  const [movingPart, setMovingPart] = useState<{
    id: string;
    cx: number;
    cy: number;
    widthPct?: number;
    heightPct?: number;
  } | null>(null);
  const diagramSurfaceRef = useRef<HTMLDivElement>(null);

  const [chassisOpen, setChassisOpen] = useState(false);
  const [cfVin, setCfVin] = useState("");
  const [cfChPre, setCfChPre] = useState("");
  const [cfFrom, setCfFrom] = useState("");
  const [cfTo, setCfTo] = useState("");
  const [cfProdF, setCfProdF] = useState("");
  const [cfProdT, setCfProdT] = useState("");
  const [cfNotes, setCfNotes] = useState("");

  /** ແກ້ໄຂຊ່ວງ chassis (ແທນ window.prompt) */
  const [chassisEditOpen, setChassisEditOpen] = useState(false);
  const [ecfId, setEcfId] = useState<string | null>(null);
  const [ecfVin, setEcfVin] = useState("");
  const [ecfChPre, setEcfChPre] = useState("");
  const [ecfFrom, setEcfFrom] = useState("");
  const [ecfTo, setEcfTo] = useState("");
  const [ecfProdF, setEcfProdF] = useState("");
  const [ecfProdT, setEcfProdT] = useState("");
  const [ecfNotes, setEcfNotes] = useState("");

  /** ແກ້ໄຂກຸ່ມ EPC */
  const [groupEditOpen, setGroupEditOpen] = useState(false);
  const [egfId, setEgfId] = useState<string | null>(null);
  const [egfName, setEgfName] = useState("");
  const [egfCode, setEgfCode] = useState("");
  const [egfSort, setEgfSort] = useState("0");

  /** ແກ້ໄຂຊື່ແຜນ */
  const [diagramRenameOpen, setDiagramRenameOpen] = useState(false);
  const [diagramRenameId, setDiagramRenameId] = useState<string | null>(null);
  const [dfRenameTitle, setDfRenameTitle] = useState("");

  const detail = detailQuery.data ?? null;

  const resetPartForm = useCallback(() => {
    setEditingPartId(null);
    setPartCallout("1");
    setPartProductId("");
    setPartSkuIds([]);
    setPartX("");
    setPartY("");
    setPartW("");
    setPartH("");
    setPartQty("");
    setPartNote("");
    setBoxDraw(null);
    setPlacementHint(
      placementMode === "box"
        ? "ລາກບົນແຜນເພື່ອວາດກອ້ງເລກອ້າງອີງ"
        : "ກົດຈຸດດຽວບົນແຜນເພື່ອວາງເລກອ້າງອີງ",
    );
  }, [placementMode]);

  const loadPartIntoForm = useCallback((p: EpcDiagramPartDTO) => {
    setEditingPartId(p.id);
    setPartCallout(p.calloutNumber);
    setPartProductId(p.catalogProductId);
    setPartSkuIds(
      p.catalogSkuIds ?? p.skus?.map((s) => s.catalogSkuId) ?? [],
    );
    setPartX(String(Number(p.xPct)));
    setPartY(String(Number(p.yPct)));
    setPartW(
      p.widthPct != null && Number(p.widthPct) > 0
        ? String(Number(p.widthPct))
        : "",
    );
    setPartH(
      p.heightPct != null && Number(p.heightPct) > 0
        ? String(Number(p.heightPct))
        : "",
    );
    setPartQty(
      p.quantityPerVehicle != null ? String(p.quantityPerVehicle) : "",
    );
    setPartNote(p.applicabilityNote ?? "");
    setBoxDraw(null);
    setPlacementHint(
      `ແກ້ໄຂເລກອ້າງອີງ #${p.calloutNumber} — ລາກກົອບຟ້າ ຫຼື ແກ້ % ດ້ານລຸ່ມ`,
    );
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset staged draw ມົນເປຍນແຜນ ຫຼື ໂໝດ
  useEffect(() => {
    setBoxDraw(null);
    resetPartForm();
  }, [placementMode, selectedDiagramId]);

  useEffect(() => {
    const onKey = (ev: globalThis.KeyboardEvent) => {
      if (ev.key !== "Escape") return;
      setBoxDraw(null);
      if (editingPartId) resetPartForm();
    };
    const target = window as unknown as Window;
    target.addEventListener("keydown", onKey);
    return () => target.removeEventListener("keydown", onKey);
  }, [editingPartId, resetPartForm]);

  const applyBoxToForm = useCallback(
    (x1: number, y1: number, x2: number, y2: number) => {
      const { cx, cy, wPct, hPct } = boxFromTwoPoints(x1, y1, x2, y2);
      setPartX(String(cx));
      setPartY(String(cy));
      setPartW(String(wPct));
      setPartH(String(hPct));
      setPlacementHint(
        `ກອ້ງ ${wPct}×${hPct}% · ກາງ (${cx}, ${cy}) — ລາກກົອບຟ້າ ຫຼື ບັນທຶກ`,
      );
    },
    [],
  );

  const handleDiagramPointerDown = (ev: ReactPointerEvent<HTMLDivElement>) => {
    if (!canCreate) return;
    if ((ev.target as HTMLElement).closest("[data-callout-marker]")) return;
    if ((ev.target as HTMLElement).closest("[data-draft-callout]")) return;
    if ((ev.target as HTMLElement).closest("[data-callout-resize]")) return;

    const surf = diagramSurfaceRef.current;
    if (!surf) return;
    const rect = surf.getBoundingClientRect();

    if (placementMode === "point") {
      const { xPct, yPct } = pctFromClientPoint(ev.clientX, ev.clientY, rect);
      setPartX(String(xPct));
      setPartY(String(yPct));
      setPartW("");
      setPartH("");
      setPlacementHint(`ຈຸດ (${xPct}, ${yPct}) — ກວດສິນຄ້າແລ້ວບັນທຶກ`);
      return;
    }

    ev.preventDefault();
    const { xPct, yPct } = pctFromClientPoint(ev.clientX, ev.clientY, rect);
    const pointerId = ev.pointerId;
    const start = { xPct, yPct };
    setBoxDraw({
      startXPct: xPct,
      startYPct: yPct,
      currentXPct: xPct,
      currentYPct: yPct,
    });
    setPlacementHint("ກຳລັງວາດກອ້ງ… ປ່ອຍເມົ້າເພື່ອຈົບ");

    const move = (pe: PointerEvent) => {
      if (pe.pointerId !== pointerId) return;
      const pt = pctFromClientPoint(pe.clientX, pe.clientY, rect);
      setBoxDraw({
        startXPct: start.xPct,
        startYPct: start.yPct,
        currentXPct: pt.xPct,
        currentYPct: pt.yPct,
      });
    };

    const up = (pe: PointerEvent) => {
      if (pe.pointerId !== pointerId) return;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setBoxDraw(null);

      const end = pctFromClientPoint(pe.clientX, pe.clientY, rect);
      const movedPx = Math.hypot(
        pe.clientX - ev.clientX,
        pe.clientY - ev.clientY,
      );
      if (movedPx < 4) {
        setPlacementHint("ລາກຂຶ້ນຫນ້ອຍໆເພື່ອວາດກອ້ງ (ບໍ່ແມ່ນແຕ່ກົດ)");
        return;
      }
      applyBoxToForm(start.xPct, start.yPct, end.xPct, end.yPct);
    };

    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const handleDraftDragStart = useCallback(
    (ev: ReactPointerEvent) => {
      if (!canCreate && !canUpdate) return;
      ev.preventDefault();
      ev.stopPropagation();

      const surf = diagramSurfaceRef.current;
      if (!surf) return;

      const startX = Number.parseFloat(partX);
      const startY = Number.parseFloat(partY);
      if (!Number.isFinite(startX) || !Number.isFinite(startY)) return;

      let wNum: number | null = null;
      let hNum: number | null = null;
      const wTrim = partW.trim();
      const hTrim = partH.trim();
      if (wTrim !== "" && hTrim !== "") {
        const bw = Number.parseFloat(wTrim);
        const bh = Number.parseFloat(hTrim);
        if (Number.isFinite(bw) && Number.isFinite(bh) && bw > 0 && bh > 0) {
          wNum = bw;
          hNum = bh;
        }
      }

      const pointerId = ev.pointerId;
      const startClientX = ev.clientX;
      const startClientY = ev.clientY;
      const anchor = clampCenterPct(startX, startY, wNum, hNum);

      const move = (pe: PointerEvent) => {
        if (pe.pointerId !== pointerId) return;
        const rect = surf.getBoundingClientRect();
        const dxPct =
          ((pe.clientX - startClientX) / Math.max(rect.width, 1e-6)) * 100;
        const dyPct =
          ((pe.clientY - startClientY) / Math.max(rect.height, 1e-6)) * 100;
        const { cx, cy } = clampCenterPct(
          anchor.cx + dxPct,
          anchor.cy + dyPct,
          wNum,
          hNum,
        );
        setPartX(String(round2Pct(cx)));
        setPartY(String(round2Pct(cy)));
      };

      const up = (pe: PointerEvent) => {
        if (pe.pointerId !== pointerId) return;
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
      };

      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    },
    [canCreate, canUpdate, partH, partW, partX, partY],
  );

  const boxDrawPreview = useMemo(() => {
    if (!boxDraw) return null;
    const { cx, cy, wPct, hPct } = boxFromTwoPoints(
      boxDraw.startXPct,
      boxDraw.startYPct,
      boxDraw.currentXPct,
      boxDraw.currentYPct,
    );
    let left = cx - wPct / 2;
    let top = cy - hPct / 2;
    left = clampPct(left, 0, Math.max(0, 100 - wPct));
    top = clampPct(top, 0, Math.max(0, 100 - hPct));
    return { left, top, wPct, hPct, cx, cy };
  }, [boxDraw]);

  const parsePartFormPayload = useCallback(() => {
    const x = Number.parseFloat(partX);
    const y = Number.parseFloat(partY);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      toast.error("ຕ້ອງມີ x% ແລະ y%");
      return null;
    }
    if (x < 0 || x > 100 || y < 0 || y > 100) {
      toast.error("x ກັບ y ຕ້ອງຢູ່ລະຫວ່າງ 0–100");
      return null;
    }

    let quantityPerVehicle: number | undefined;
    if (partQty.trim() !== "") {
      const q = Number.parseInt(partQty, 10);
      if (!Number.isFinite(q) || q <= 0) {
        toast.error("ຈຳນວນ ຕ້ອງເປັນຕົວເລກບວກ");
        return null;
      }
      quantityPerVehicle = q;
    }

    const wTrim = partW.trim();
    const hTrim = partH.trim();
    let widthPct: number | undefined;
    let heightPct: number | undefined;
    if (wTrim !== "" || hTrim !== "") {
      const bw = Number.parseFloat(wTrim);
      const bh = Number.parseFloat(hTrim);
      if (!Number.isFinite(bw) || !Number.isFinite(bh) || bw <= 0 || bh <= 0) {
        toast.error(
          "ກຳນົດກອ້ງ: ກວ້າງ ແລະ ສູງ ຕ້ອງມີຄ່າພ້ອມກັນເປັນເລກບວກ",
        );
        return null;
      }
      if (bw > 100 || bh > 100) {
        toast.error("ກວ້າງ/ສູງ ຕ້ອງບໍ່ເກີນ 100");
        return null;
      }
      widthPct = bw;
      heightPct = bh;
    }

    return {
      xPct: x,
      yPct: y,
      widthPct,
      heightPct,
      calloutNumber: partCallout.trim() || "1",
      quantityPerVehicle,
      applicabilityNote: partNote.trim() ? partNote.trim() : undefined,
    };
  }, [partCallout, partH, partNote, partQty, partW, partX, partY]);

  const handleSaveCallout = async () => {
    const payload = parsePartFormPayload();
    if (!payload) return;

    if (editingPartId) {
      if (!canUpdate) return;
      if (partSkuIds.length === 0) {
        toast.error("ຕ້ອງເລືອກຢ່າງໜ້ອຍ 1 SKU");
        return;
      }
      try {
        await updatePart.mutateAsync({
          id: editingPartId,
          input: {
            calloutNumber: payload.calloutNumber,
            catalogSkuIds: partSkuIds,
            xPct: payload.xPct,
            yPct: payload.yPct,
            ...(payload.widthPct !== undefined &&
            payload.heightPct !== undefined
              ? {
                  widthPct: payload.widthPct,
                  heightPct: payload.heightPct,
                }
              : { widthPct: null, heightPct: null }),
            quantityPerVehicle: payload.quantityPerVehicle ?? null,
            applicabilityNote: payload.applicabilityNote ?? null,
          },
        });
        resetPartForm();
      } catch {
        /* */
      }
      return;
    }

    const productId = partProductId.trim();
    if (!productId) {
      toast.error("ຕ້ອງເລືອກສິນຄ້າໃນແຄຕາລອກ");
      return;
    }
    if (partSkuIds.length === 0) {
      toast.error("ຕ້ອງເລືອກຢ່າງໜ້ອຍ 1 SKU");
      return;
    }
    if (!canCreate || !selectedDiagramId) return;

    try {
      await createPart.mutateAsync({
        epcDiagramId: selectedDiagramId,
        catalogProductId: productId,
        catalogSkuIds: partSkuIds,
        calloutNumber: payload.calloutNumber,
        xPct: payload.xPct,
        yPct: payload.yPct,
        ...(payload.widthPct !== undefined && payload.heightPct !== undefined
          ? {
              widthPct: payload.widthPct,
              heightPct: payload.heightPct,
            }
          : {}),
        quantityPerVehicle: payload.quantityPerVehicle,
        applicabilityNote: payload.applicabilityNote,
      });
      resetPartForm();
    } catch {
      /* */
    }
  };

  const partsForDiagramDisplay = useMemo(() => {
    const rows = partsQuery.data ?? [];
    if (!movingPart) return rows;
    return rows.map((r) =>
      r.id === movingPart.id
        ? {
            ...r,
            xPct: movingPart.cx,
            yPct: movingPart.cy,
            ...(movingPart.widthPct != null && movingPart.heightPct != null
              ? {
                  widthPct: movingPart.widthPct,
                  heightPct: movingPart.heightPct,
                }
              : {}),
          }
        : r,
    );
  }, [partsQuery.data, movingPart]);

  const calloutDraftPreviewFromForm = useMemo(() => {
    const x = Number.parseFloat(partX);
    const y = Number.parseFloat(partY);
    if (
      !Number.isFinite(x) ||
      !Number.isFinite(y) ||
      x < 0 ||
      x > 100 ||
      y < 0 ||
      y > 100
    ) {
      return null;
    }

    let wPct: number | null = null;
    let hPct: number | null = null;
    const wTrim = partW.trim();
    const hTrim = partH.trim();
    if (wTrim !== "" || hTrim !== "") {
      const bw = Number.parseFloat(wTrim);
      const bh = Number.parseFloat(hTrim);
      if (
        Number.isFinite(bw) &&
        Number.isFinite(bh) &&
        bw > 0 &&
        bw <= 100 &&
        bh > 0 &&
        bh <= 100
      ) {
        wPct = bw;
        hPct = bh;
      }
    }

    const { cx, cy } = clampCenterPct(x, y, wPct, hPct);
    const labelRaw = partCallout.trim();
    const label = labelRaw.length > 0 ? labelRaw.slice(0, 10) : "⋯";
    return { cx, cy, wPct, hPct, label };
  }, [partX, partY, partW, partH, partCallout]);

  const syncEditingFormFromBox = useCallback(
    (partId: string, cx: number, cy: number, wPct: number, hPct: number) => {
      if (editingPartId !== partId) return;
      setPartX(String(round2Pct(cx)));
      setPartY(String(round2Pct(cy)));
      setPartW(String(round2Pct(wPct)));
      setPartH(String(round2Pct(hPct)));
    },
    [editingPartId],
  );

  const handleSavedPartDragStart = useCallback(
    (part: EpcDiagramPartDTO, ev: ReactPointerEvent) => {
      if (!canUpdate || updatePart.isPending) return;
      ev.preventDefault();
      ev.stopPropagation();
      const surf = diagramSurfaceRef.current;
      if (!surf) return;

      let wNum: number | null = null;
      let hNum: number | null = null;
      if (part.widthPct != null && part.heightPct != null) {
        const tw = Number(part.widthPct);
        const th = Number(part.heightPct);
        if (Number.isFinite(tw) && Number.isFinite(th) && tw > 0 && th > 0) {
          wNum = tw;
          hNum = th;
        }
      }

      const anchorCx = Number(part.xPct);
      const anchorCy = Number(part.yPct);
      if (!Number.isFinite(anchorCx) || !Number.isFinite(anchorCy)) return;

      const startClientX = ev.clientX;
      const startClientY = ev.clientY;
      const pointerId = ev.pointerId;
      let didMovePx = false;
      const moveThresh = 3;

      const init = clampCenterPct(anchorCx, anchorCy, wNum, hNum);
      const liveDragRef = { cx: init.cx, cy: init.cy };
      setMovingPart({
        id: part.id,
        cx: init.cx,
        cy: init.cy,
        ...(wNum != null && hNum != null
          ? { widthPct: wNum, heightPct: hNum }
          : {}),
      });

      const move = (pe: PointerEvent) => {
        if (pe.pointerId !== pointerId) return;
        if (
          Math.abs(pe.clientX - startClientX) > moveThresh ||
          Math.abs(pe.clientY - startClientY) > moveThresh
        ) {
          didMovePx = true;
        }
        const rect = surf.getBoundingClientRect();
        const mw = Math.max(rect.width, 1e-6);
        const mh = Math.max(rect.height, 1e-6);
        const dxPct = ((pe.clientX - startClientX) / mw) * 100;
        const dyPct = ((pe.clientY - startClientY) / mh) * 100;
        const { cx: nextCx, cy: nextCy } = clampCenterPct(
          anchorCx + dxPct,
          anchorCy + dyPct,
          wNum,
          hNum,
        );
        liveDragRef.cx = nextCx;
        liveDragRef.cy = nextCy;
        setMovingPart({
          id: part.id,
          cx: nextCx,
          cy: nextCy,
          ...(wNum != null && hNum != null
            ? { widthPct: wNum, heightPct: hNum }
            : {}),
        });
        if (wNum != null && hNum != null) {
          syncEditingFormFromBox(part.id, nextCx, nextCy, wNum, hNum);
        } else {
          if (editingPartId === part.id) {
            setPartX(String(round2Pct(nextCx)));
            setPartY(String(round2Pct(nextCy)));
          }
        }
      };

      const up = (pe: PointerEvent) => {
        if (pe.pointerId !== pointerId) return;
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);

        const ax = round2Pct(anchorCx);
        const ay = round2Pct(anchorCy);
        const fx = round2Pct(liveDragRef.cx);
        const fy = round2Pct(liveDragRef.cy);

        setMovingPart(null);
        if (
          didMovePx &&
          (Math.abs(fx - ax) > 0.01 || Math.abs(fy - ay) > 0.01)
        ) {
          void updatePart.mutateAsync({
            id: part.id,
            input: { xPct: fx, yPct: fy },
          });
        }
      };

      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    },
    [canUpdate, editingPartId, syncEditingFormFromBox, updatePart],
  );

  const handleSavedPartResizeStart = useCallback(
    (part: EpcDiagramPartDTO, corner: ResizeCorner, ev: ReactPointerEvent) => {
      if (!canUpdate || updatePart.isPending) return;
      ev.preventDefault();
      ev.stopPropagation();
      const surf = diagramSurfaceRef.current;
      if (!surf) return;

      const anchorCx = Number(part.xPct);
      const anchorCy = Number(part.yPct);
      const anchorW = Number(part.widthPct);
      const anchorH = Number(part.heightPct);
      if (
        !Number.isFinite(anchorCx) ||
        !Number.isFinite(anchorCy) ||
        !Number.isFinite(anchorW) ||
        !Number.isFinite(anchorH) ||
        anchorW <= 0 ||
        anchorH <= 0
      ) {
        return;
      }

      const fixed = fixedCornerForResize(
        anchorCx,
        anchorCy,
        anchorW,
        anchorH,
        corner,
      );
      const pointerId = ev.pointerId;
      let didMovePx = false;
      const moveThresh = 3;
      const startClientX = ev.clientX;
      const startClientY = ev.clientY;
      const liveRef = {
        cx: anchorCx,
        cy: anchorCy,
        wPct: anchorW,
        hPct: anchorH,
      };

      setMovingPart({
        id: part.id,
        cx: anchorCx,
        cy: anchorCy,
        widthPct: anchorW,
        heightPct: anchorH,
      });
      setPlacementHint(`ປັບຂະໜາດເລກອ້າງອີງ #${part.calloutNumber}…`);

      const move = (pe: PointerEvent) => {
        if (pe.pointerId !== pointerId) return;
        if (
          Math.abs(pe.clientX - startClientX) > moveThresh ||
          Math.abs(pe.clientY - startClientY) > moveThresh
        ) {
          didMovePx = true;
        }
        const rect = surf.getBoundingClientRect();
        const pt = pctFromClientPoint(pe.clientX, pe.clientY, rect);
        const next = boxFromTwoPoints(fixed.xPct, fixed.yPct, pt.xPct, pt.yPct);
        const clamped = clampCenterPct(
          next.cx,
          next.cy,
          next.wPct,
          next.hPct,
        );
        liveRef.cx = clamped.cx;
        liveRef.cy = clamped.cy;
        liveRef.wPct = next.wPct;
        liveRef.hPct = next.hPct;
        setMovingPart({
          id: part.id,
          cx: clamped.cx,
          cy: clamped.cy,
          widthPct: next.wPct,
          heightPct: next.hPct,
        });
        syncEditingFormFromBox(
          part.id,
          clamped.cx,
          clamped.cy,
          next.wPct,
          next.hPct,
        );
      };

      const up = (pe: PointerEvent) => {
        if (pe.pointerId !== pointerId) return;
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        setMovingPart(null);

        if (!didMovePx) {
          setPlacementHint("");
          return;
        }

        const fx = round2Pct(liveRef.cx);
        const fy = round2Pct(liveRef.cy);
        const fw = round2Pct(liveRef.wPct);
        const fh = round2Pct(liveRef.hPct);
        const changed =
          Math.abs(fx - anchorCx) > 0.01 ||
          Math.abs(fy - anchorCy) > 0.01 ||
          Math.abs(fw - anchorW) > 0.01 ||
          Math.abs(fh - anchorH) > 0.01;

        if (changed) {
          void updatePart.mutateAsync({
            id: part.id,
            input: {
              xPct: fx,
              yPct: fy,
              widthPct: fw,
              heightPct: fh,
            },
          });
          setPlacementHint(
            `ອັບເດດຂະໜາດ #${part.calloutNumber} → ${fw}×${fh}%`,
          );
        } else {
          setPlacementHint("");
        }
      };

      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    },
    [canUpdate, syncEditingFormFromBox, updatePart],
  );

  const openCreateChassis = () => {
    setCfVin("");
    setCfChPre(detail?.modelCode?.trim() ?? "");
    setCfFrom("");
    setCfTo("");
    setCfProdF("");
    setCfProdT("");
    setCfNotes("");
    setChassisOpen(true);
  };

  const resetChassisEditForm = () => {
    setEcfId(null);
    setEcfVin("");
    setEcfChPre("");
    setEcfFrom("");
    setEcfTo("");
    setEcfProdF("");
    setEcfProdT("");
    setEcfNotes("");
  };

  const openEditChassis = (r: VehicleChassisRangeDTO) => {
    setEcfId(r.id);
    setEcfVin(r.vinPrefix ?? "");
    setEcfChPre(r.chassisPrefix ?? "");
    setEcfFrom(r.chassisFrom ?? "");
    setEcfTo(r.chassisTo ?? "");
    setEcfProdF(toDateInputValue(r.productionFrom));
    setEcfProdT(toDateInputValue(r.productionTo));
    setEcfNotes(r.notes ?? "");
    setChassisEditOpen(true);
  };

  const resetGroupEditForm = () => {
    setEgfId(null);
    setEgfName("");
    setEgfCode("");
    setEgfSort("0");
  };

  const openEditGroup = (g: EpcGroupDTO) => {
    setEgfId(g.id);
    setEgfName(g.groupName);
    setEgfCode(g.groupCode ?? "");
    setEgfSort(String(g.sortOrder ?? 0));
    setGroupEditOpen(true);
  };

  const resetDiagramRenameForm = () => {
    setDiagramRenameId(null);
    setDfRenameTitle("");
  };

  if (!configurationId || !makeId || !modelId)
    return (
      <Main>
        <p className="text-muted-foreground">ບໍ່ພົບສະເປັກລົດ</p>
      </Main>
    );

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ແຄຕາລອກ", to: "/app/catalog" },
            { label: "ຍີ່ຫໍ້ລົດ", to: "/app/catalog/vehicles" },
            {
              label: detail?.makeName ?? "…",
              to: `/app/catalog/vehicles/${makeId}`,
            },
            {
              label: detail?.modelName ?? "…",
              to: `/app/catalog/vehicles/${makeId}/models/${modelId}`,
            },
            { label: "EPC" },
          ]}
        />
      </Header>

      <Main className="space-y-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              EPC — {detail?.makeName} {detail?.modelName}
              {detail?.modelCode ? (
                <span className="ml-2 font-mono font-normal text-base text-muted-foreground">
                  ({detail.modelCode})
                </span>
              ) : null}
            </h2>
            <p className="text-muted-foreground text-sm">
              ລະຫັດສະເປັກ/ຕົວເລືອກ · ເລກຖັງ/VIN · ກຸ່ມ EPC · ແຜນຮູບ · ເລກອ້າງອີງ %
            </p>
            {detail && (
              <p className="mt-1 text-muted-foreground text-xs">
                {formatConfigurationPeriodRange(
                  detail.yearFrom,
                  detail.yearTo,
                  detail.monthFrom,
                  detail.monthTo,
                ) ?? "—"}{" "}
                · {detail.engine ?? "—"}
                {detail.marketCode ? ` · ${detail.marketCode}` : ""}
                {detail.transmissionCode ? ` · ${detail.transmissionCode}` : ""}
                {detail.engineCode ? ` · ເຄື່ອງ ${detail.engineCode}` : ""}
                {detail.gradeCode ? ` · ${detail.gradeCode}` : ""}
                {detail.adminNotes ? ` · ${detail.adminNotes}` : ""}
              </p>
            )}
          </div>
          <Link
            className="text-primary text-sm underline underline-offset-2"
            to="/app/catalog/vehicles/$makeId/models/$modelId"
            params={{ makeId, modelId }}
          >
            ← ກັບໄປລາຍການສະເປັກລົດ
          </Link>
        </div>

        <VehicleModelCodesSection modelId={modelId} />

        <VehicleConfigurationCodeAssignmentsSection
          configurationId={configId}
        />

        <section className="space-y-3 rounded-xl border bg-card p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold text-base">
              ຊ່ວງເລກຖັງ / VIN / ຊ່ວງຜະລິດ
            </h3>
            {canCreate && (
              <Button
                type="button"
                size="sm"
                onClick={openCreateChassis}
              >
                ເພີ່ມຊ່ວງ
              </Button>
            )}
          </div>
          {chassisQuery.isLoading ? (
            <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b bg-muted/30">
                  <tr>
                    <th className="py-2 pr-2">ຄຳນຳໜ້າ VIN</th>
                    <th className="py-2 pr-2">ເລກຖັງ</th>
                    <th className="py-2 pr-2">ຈາກ–ເຖິງ</th>
                    <th className="py-2 pr-2">ຜະລິດ</th>
                    <th className="py-2 pr-2">ໝາຍເຫດ</th>
                    {canManage && <th className="py-2" />}
                  </tr>
                </thead>
                <tbody>
                  {(chassisQuery.data ?? []).map((r) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="py-2 pr-2 font-mono text-xs">
                        {r.vinPrefix ?? "—"}
                      </td>
                      <td className="py-2 pr-2 text-xs">
                        {r.chassisPrefix ?? "—"}
                      </td>
                      <td className="py-2 pr-2 text-xs">
                        {r.chassisFrom ?? "?"} – {r.chassisTo ?? "?"}
                      </td>
                      <td className="py-2 pr-2 text-xs">
                        {toDateInputValue(r.productionFrom) || "—"} →{" "}
                        {toDateInputValue(r.productionTo) || "—"}
                      </td>
                      <td className="py-2 pr-2 text-xs">{r.notes ?? "—"}</td>
                      {canManage && (
                        <td className="py-2 text-right">
                          <RowActions
                            maxInline={2}
                            actions={[
                              ...(canUpdate
                                ? [
                                    {
                                      label: "ແກ້ໄຂ",
                                      onClick: () => openEditChassis(r),
                                    },
                                  ]
                                : []),
                              ...(canDelete
                                ? [
                                    {
                                      label: "ລຶບ",
                                      variant: "destructive" as const,
                                      onClick: async () => {
                                        const ok = await confirm({
                                          title: "ລຶບຊ່ວງ",
                                          description: "ຕ້ອງການລຶບບໍ?",
                                          actionText: "ລຶບ",
                                          ActionProps: {
                                            variant: "destructive",
                                          },
                                        });
                                        if (ok)
                                          await deleteChassis.mutateAsync(r.id);
                                      },
                                    },
                                  ]
                                : []),
                            ]}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="space-y-3 rounded-xl border bg-card p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold text-base">ກຸ່ມ EPC</h3>
            {canCreate && (
              <Button
                type="button"
                size="sm"
                onClick={() => setGroupOpen(true)}
              >
                ເພີ່ມກຸ່ມ
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b bg-muted/30">
                <tr>
                  <th className="py-2 pr-2">ກຸ່ມ</th>
                  <th className="py-2 pr-2">ລະຫັດ</th>
                  <th className="py-2 pr-2">ລໍາດັບ</th>
                  {(canDelete || canUpdate) && <th className="py-2" />}
                </tr>
              </thead>
              <tbody>
                {(epcGroupsQuery.data ?? []).map((g) => (
                  <tr
                    key={g.id}
                    className={`cursor-pointer border-b last:border-0 hover:bg-muted/30 ${
                      selectedGroupId === g.id ? "bg-muted/40" : ""
                    }`}
                    onClick={() => {
                      setSelectedGroupId(g.id);
                      setSelectedDiagramId("");
                    }}
                  >
                    <td className="py-2 pr-2">
                      <span
                        style={{
                          paddingLeft: `${epcGroupDepth(epcGroupsQuery.data ?? [], g.id) * 12}px`,
                        }}
                      >
                        {g.groupName}
                      </span>
                    </td>
                    <td className="py-2 pr-2">{g.groupCode ?? "—"}</td>
                    <td className="py-2 pr-2">{g.sortOrder}</td>
                    {(canDelete || canUpdate) && (
                      <td
                        className="py-2 text-right"
                        onMouseDown={(ev) => ev.stopPropagation()}
                      >
                        <RowActions
                          maxInline={2}
                          actions={[
                            ...(canUpdate
                              ? [
                                  {
                                    label: "ແກ້ໄຂ",
                                    onClick: () => openEditGroup(g),
                                  },
                                ]
                              : []),
                            ...(canDelete
                              ? [
                                  {
                                    label: "ລຶບທັງແຜນ",
                                    variant: "destructive" as const,
                                    onClick: async () => {
                                      const ok = await confirm({
                                        title: "ລຶບກຸ່ມ EPC",
                                        description:
                                          "ຈະລຶບກຸ່ມຍ່ອຍ ແລະ ແຜນຜັງ ທັງໝົດ. ຕ້ອງການບໍ?",
                                        actionText: "ລຶບ",
                                        ActionProps: { variant: "destructive" },
                                      });
                                      if (ok)
                                        await deleteGroup.mutateAsync(g.id);
                                    },
                                  },
                                ]
                              : []),
                          ]}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
          <div className="space-y-3 rounded-xl border bg-card p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-semibold text-base">ແຜນຜັງນຳເຂົ້າ</h3>
              {canCreate && selectedGroupId && (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    setDfTitle("");
                    setDfDiagramImageFile(null);
                    setDfSort("0");
                    setDiagramOpen(true);
                  }}
                >
                  ເພີ່ມແຜນ
                </Button>
              )}
            </div>
            {!selectedGroupId ? (
              <p className="text-muted-foreground text-sm">ເລືອກກຸ່ມ EPC</p>
            ) : diagramsQuery.isLoading ? (
              <p className="text-muted-foreground text-sm">ໂຫຼດແຜນ...</p>
            ) : (
              <ul className="space-y-1">
                {(diagramsQuery.data ?? []).map((d) => (
                  <li key={d.id} className="flex items-stretch gap-1">
                    <button
                      type="button"
                      className={`flex min-w-0 flex-1 items-center rounded-md border px-2 py-1.5 text-left text-sm ${
                        selectedDiagramId === d.id
                          ? "border-primary bg-primary/5"
                          : "border-border"
                      }`}
                      onClick={() => setSelectedDiagramId(d.id)}
                    >
                      <span className="truncate">
                        {d.title ?? `${d.imageUrl.slice(0, 40)}…`}
                      </span>
                    </button>
                    {canDelete && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="shrink-0 text-destructive"
                        onClick={async () => {
                          const ok = await confirm({
                            title: "ລຶບແຜນ",
                            description: "ລຶບຮູບ ແລະ ເລກອ້າງອີງທັງໝົດ",
                            actionText: "ລຶບ",
                            ActionProps: { variant: "destructive" },
                          });
                          if (ok) await deleteDiagram.mutateAsync(d.id);
                        }}
                      >
                        ລຶບ
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {selectedDiagram && canUpdate && (
              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setDiagramRenameId(selectedDiagram.id);
                    setDfRenameTitle(selectedDiagram.title ?? "");
                    setDiagramRenameOpen(true);
                  }}
                >
                  ແກ້ຊື່ແຜນ
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-3 rounded-xl border bg-card p-4">
            <h3 className="font-semibold text-base">
              ວາງເລກອ້າງອີງບົນແຜນ (% ຕາມຮູບ)
            </h3>
            {!selectedDiagram ? (
              <p className="text-muted-foreground text-sm">ເລືອກແຜນ</p>
            ) : (
              <>
                {canCreate || canUpdate ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="inline-flex rounded-md border p-0.5">
                        <Button
                          type="button"
                          size="sm"
                          variant={
                            placementMode === "box" ? "default" : "ghost"
                          }
                          className="h-8"
                          onClick={() => setPlacementMode("box")}
                        >
                          ວາດກອ້ງ
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={
                            placementMode === "point" ? "default" : "ghost"
                          }
                          className="h-8"
                          onClick={() => setPlacementMode("point")}
                        >
                          ຈຸດດຽວ
                        </Button>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setBoxDraw(null);
                          resetPartForm();
                        }}
                      >
                        ລ້າງຟອร์ມ
                      </Button>
                      {editingPartId ? (
                        <span className="rounded-md bg-amber-100 px-2 py-1 text-amber-900 text-xs">
                          ແກ້ໄຂເລກອ້າງອີງ
                        </span>
                      ) : null}
                    </div>
                    {placementHint ? (
                      <p className="rounded-md border border-dashed bg-muted/40 px-3 py-2 text-muted-foreground text-xs">
                        {placementHint}
                        <span className="ml-2 opacity-70">
                          · ກົດ Esc ຍົກເລີກແກ້ໄຂ
                        </span>
                      </p>
                    ) : (
                      <p className="text-muted-foreground text-xs">
                        ກົອບຟ້າ = ຕົວຢ່າງກ່ອນບັນທຶກ (ລາກໄດ້) ·                         ກົອບແດງ =
                        ເລກອ້າງອີງບັນທຶກແລ້ວ (ລາກເພື່ອຍ້າຍ · ຈຸດຂອບເພື່ອປັບຂະໜາດ) ·
                        ກົດແຖວໃນຕາຕະລາງເພື່ອແກ້ໄຂ
                      </p>
                    )}
                    <div
                      ref={diagramSurfaceRef}
                      className={`relative block w-full min-w-0 rounded-md border bg-transparent text-left ${canCreate ? "cursor-crosshair touch-none" : ""}`}
                      {...(canCreate
                        ? {
                            onPointerDown: handleDiagramPointerDown,
                          }
                        : {})}
                    >
                      <img
                        alt={selectedDiagram.title ?? "ແຜນພາບ EPC"}
                        src={selectedDiagramImageSrc ?? undefined}
                        className="pointer-events-none block h-auto max-h-[min(75vh,760px)] w-full rounded-md bg-muted object-contain"
                        draggable={false}
                      />
                      {boxDrawPreview ? (
                        <div className="pointer-events-none absolute inset-0 z-4">
                          <div
                            className="absolute border-2 border-amber-500 border-dashed bg-amber-300/25"
                            style={{
                              left: `${boxDrawPreview.left}%`,
                              top: `${boxDrawPreview.top}%`,
                              width: `${boxDrawPreview.wPct}%`,
                              height: `${boxDrawPreview.hPct}%`,
                            }}
                          />
                          <div
                            className="absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-amber-600 bg-amber-400"
                            style={{
                              left: `${boxDrawPreview.cx}%`,
                              top: `${boxDrawPreview.cy}%`,
                            }}
                          />
                        </div>
                      ) : null}
                      <EpcDiagramPartMarkers
                        parts={partsForDiagramDisplay}
                        canDragMove={canUpdate}
                        highlightPartId={editingPartId}
                        onSavedPartDragStart={handleSavedPartDragStart}
                        onSavedPartResizeStart={handleSavedPartResizeStart}
                      />
                      {calloutDraftPreviewFromForm ? (
                        <CalloutFormPreviewOverlay
                          cx={calloutDraftPreviewFromForm.cx}
                          cy={calloutDraftPreviewFromForm.cy}
                          wPct={calloutDraftPreviewFromForm.wPct}
                          hPct={calloutDraftPreviewFromForm.hPct}
                          label={calloutDraftPreviewFromForm.label}
                          interactive={canCreate || canUpdate}
                          onDragStart={handleDraftDragStart}
                        />
                      ) : null}
                    </div>
                  </div>
                ) : (
                  <div
                    ref={diagramSurfaceRef}
                    className="relative block w-full min-w-0 rounded-md border"
                  >
                    <img
                      alt={selectedDiagram.title ?? "ແຜນພາບ EPC"}
                      src={selectedDiagramImageSrc ?? undefined}
                      className="block h-auto max-h-[min(75vh,760px)] w-full rounded-md bg-muted object-contain"
                    />
                    <EpcDiagramPartMarkers
                      parts={partsForDiagramDisplay}
                      canDragMove={canUpdate}
                      highlightPartId={editingPartId}
                      onSavedPartDragStart={handleSavedPartDragStart}
                      onSavedPartResizeStart={handleSavedPartResizeStart}
                    />
                    {calloutDraftPreviewFromForm ? (
                      <CalloutFormPreviewOverlay
                        cx={calloutDraftPreviewFromForm.cx}
                        cy={calloutDraftPreviewFromForm.cy}
                        wPct={calloutDraftPreviewFromForm.wPct}
                        hPct={calloutDraftPreviewFromForm.hPct}
                        label={calloutDraftPreviewFromForm.label}
                      />
                    ) : null}
                  </div>
                )}
                <div className="grid gap-2 sm:grid-cols-2">
                  <div>
                    <span className="text-muted-foreground text-xs">
                      x % (ຈຸດກາງ ຫຼື ຈຸດ)
                    </span>
                    <Input
                      value={partX}
                      onChange={(e) => setPartX(e.target.value)}
                    />
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">
                      y % (ຈຸດກາງ ຫຼື ຈຸດ)
                    </span>
                    <Input
                      value={partY}
                      onChange={(e) => setPartY(e.target.value)}
                    />
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">
                      ກວ້າງ % (ເວັ້ນວ່າງ = ຈຸດດຽວ)
                    </span>
                    <Input
                      value={partW}
                      placeholder="ເຊັ່ນ 12"
                      onChange={(e) => setPartW(e.target.value)}
                    />
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">
                      ສູງ % (ເວັ້ນວ່າງ = ຈຸດດຽວ)
                    </span>
                    <Input
                      value={partH}
                      placeholder="ເຊັ່ນ 8"
                      onChange={(e) => setPartH(e.target.value)}
                    />
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">
                      ເລກອ້າງອີງ #
                    </span>
                    <Input
                      value={partCallout}
                      onChange={(e) => setPartCallout(e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <EpcCalloutProductSkuPicker
                      configurationId={configId}
                      productId={partProductId}
                      onProductIdChange={setPartProductId}
                      selectedSkuIds={partSkuIds}
                      onSelectedSkuIdsChange={setPartSkuIds}
                      lockProduct={Boolean(editingPartId)}
                    />
                  </div>
                  <div>
                    <span className="text-muted-foreground text-xs">
                      ຈຳນວນ/ຄັນ
                    </span>
                    <Input
                      value={partQty}
                      onChange={(e) => setPartQty(e.target.value)}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <span className="text-muted-foreground text-xs">
                      ໝາຍເຫດ
                    </span>
                    <Input
                      value={partNote}
                      onChange={(e) => setPartNote(e.target.value)}
                    />
                  </div>
                </div>
                {(canCreate || (canUpdate && editingPartId)) && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      disabled={
                        !selectedDiagramId ||
                        createPart.isPending ||
                        updatePart.isPending
                      }
                      onClick={() => void handleSaveCallout()}
                    >
                      {editingPartId ? "ອັບເດດເລກອ້າງອີງ" : "ບັນທຶກເລກອ້າງອີງ"}
                    </Button>
                    {editingPartId ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={resetPartForm}
                      >
                        ຍົກເລີກແກ້ໄຂ
                      </Button>
                    ) : null}
                  </div>
                )}
                <div className="overflow-x-auto border-t pt-3">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="py-1 pr-2">#</th>
                        <th className="py-1 pr-2">ສິນຄ້າ / SKU</th>
                        <th className="py-1 pr-2">%</th>
                        <th className="py-1 pr-2">ຈຳນວນ</th>
                        {canDelete && <th className="py-1" />}
                      </tr>
                    </thead>
                    <tbody>
                      {(partsQuery.data ?? []).map((p) => (
                        <tr
                          key={p.id}
                          className={`border-b last:border-0 ${editingPartId === p.id ? "bg-amber-50/80" : "cursor-pointer hover:bg-muted/40"}`}
                          onClick={() => {
                            if (canUpdate) loadPartIntoForm(p);
                          }}
                        >
                          <td className="py-1 pr-2">{p.calloutNumber}</td>
                          <td className="py-1 pr-2">
                            <p className="font-medium text-xs">
                              {p.productName ??
                                `${p.catalogProductId.slice(0, 8)}…`}
                            </p>
                            {p.skus && p.skus.length > 0 ? (
                              <p className="mt-0.5 font-mono text-[10px] text-muted-foreground">
                                {p.skus
                                  .map((s) => s.oemPartNo ?? s.catalogSkuId.slice(0, 6))
                                  .join(" · ")}
                              </p>
                            ) : null}
                          </td>
                          <td className="py-1 pr-2 text-xs">
                            {p.widthPct != null &&
                            p.heightPct != null &&
                            typeof p.widthPct === "number" &&
                            typeof p.heightPct === "number" &&
                            p.widthPct > 0 &&
                            p.heightPct > 0 ? (
                              <span>
                                {Number(p.xPct).toFixed(1)},
                                {Number(p.yPct).toFixed(1)} · ກອ້ງ{" "}
                                {Number(p.widthPct).toFixed(1)}×
                                {Number(p.heightPct).toFixed(1)}
                              </span>
                            ) : (
                              <span>
                                {Number(p.xPct).toFixed(1)},
                                {Number(p.yPct).toFixed(1)} · ຈຸດ
                              </span>
                            )}
                          </td>
                          <td className="py-1 pr-2">
                            {p.quantityPerVehicle ?? "—"}
                          </td>
                          {canDelete && (
                            <td
                              className="py-1 text-right"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={async () => {
                                  const ok = await confirm({
                                    title: "ລຶບເລກອ້າງອີງ",
                                    description: "ຕ້ອງການລຶບບໍ?",
                                    actionText: "ລຶບ",
                                    ActionProps: { variant: "destructive" },
                                  });
                                  if (ok) await deletePart.mutateAsync(p.id);
                                }}
                              >
                                ລຶບ
                              </Button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </section>
      </Main>

      <Modal
        open={chassisOpen}
        onOpenChange={setChassisOpen}
        title="ເພີ່ມຊ່ວງເລກຖັງ"
        size="md"
      >
        <div className="grid gap-2">
          <Input
            placeholder="ຄຳນຳໜ້າ VIN"
            value={cfVin}
            onChange={(e) => setCfVin(e.target.value)}
          />
          <Input
            placeholder="ຄຳນຳໜ້າເລກຖັງ"
            value={cfChPre}
            onChange={(e) => setCfChPre(e.target.value)}
          />
          {detail?.modelCode ? (
            <p className="text-muted-foreground text-xs">
              ເຕີມອັດຕະໂນມັດຈາກລະຫັດລຸ້ນ{" "}
              <span className="font-mono">{detail.modelCode}</span> — ແກ້ໄຂໄດ້ຖ້າ
              ຄຳນຳໜ້າຕ່າງກັນ
            </p>
          ) : null}
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="ເລກຖັງ ຈາກ"
              value={cfFrom}
              onChange={(e) => setCfFrom(e.target.value)}
            />
            <Input
              placeholder="ເລກຖັງ ຫາ"
              value={cfTo}
              onChange={(e) => setCfTo(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <span className="block text-muted-foreground text-xs">
                ຜະລິດ ຕັ້ງແຕ່
              </span>
              <Input
                type="date"
                value={cfProdF}
                onChange={(e) => setCfProdF(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <span className="block text-muted-foreground text-xs">
                ຜະລິດ ຫາວັນທີ່
              </span>
              <Input
                type="date"
                value={cfProdT}
                onChange={(e) => setCfProdT(e.target.value)}
              />
            </div>
          </div>
          <Input
            placeholder="ໝາຍເຫດ"
            value={cfNotes}
            onChange={(e) => setCfNotes(e.target.value)}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setChassisOpen(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="button"
              disabled={createChassis.isPending}
              onClick={async () => {
                try {
                  await createChassis.mutateAsync({
                    vinPrefix: cfVin.trim() ? cfVin.trim() : null,
                    chassisPrefix: cfChPre.trim() ? cfChPre.trim() : null,
                    chassisFrom: cfFrom.trim() ? cfFrom.trim() : null,
                    chassisTo: cfTo.trim() ? cfTo.trim() : null,
                    productionFrom: cfProdF.trim() ? cfProdF.trim() : undefined,
                    productionTo: cfProdT.trim() ? cfProdT.trim() : undefined,
                    notes: cfNotes.trim() ? cfNotes.trim() : null,
                  });
                  setChassisOpen(false);
                  setCfVin("");
                  setCfChPre("");
                  setCfFrom("");
                  setCfTo("");
                  setCfProdF("");
                  setCfProdT("");
                  setCfNotes("");
                } catch {
                  /* */
                }
              }}
            >
              ບັນທຶກ
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={groupOpen}
        onOpenChange={setGroupOpen}
        title="ເພີ່ມກຸ່ມ EPC"
        size="md"
      >
        <div className="grid gap-2">
          <Input
            placeholder="ຊື່ກຸ່ມ *"
            value={gfName}
            onChange={(e) => setGfName(e.target.value)}
          />
          <Input
            placeholder="ລະຫັດກຸ່ມ"
            value={gfCode}
            onChange={(e) => setGfCode(e.target.value)}
          />
          <Input
            placeholder="ລໍາດັບ (ຕົວເລກ)"
            value={gfSort}
            onChange={(e) => setGfSort(e.target.value)}
          />
          <div>
            <span className="text-muted-foreground text-xs">ກຸ່ມແມ່</span>
            <SimpleSelect
              value={gfParent}
              onValueChange={setGfParent}
              options={[
                { value: "__root__", label: "(ບໍ່ມີ — root)" },
                ...(epcGroupsQuery.data ?? []).map((g) => ({
                  value: g.id,
                  label: g.groupName,
                })),
              ]}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setGroupOpen(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="button"
              disabled={createGroup.isPending}
              onClick={async () => {
                if (!gfName.trim()) {
                  toast.error("ຕ້ອງມີຊື່ກຸ່ມ");
                  return;
                }
                const so = Number.parseInt(gfSort, 10);
                try {
                  await createGroup.mutateAsync({
                    vehicleConfigurationId: configId,
                    parentGroupId: gfParent !== "__root__" ? gfParent : null,
                    groupCode: gfCode.trim() ? gfCode.trim() : null,
                    groupName: gfName.trim(),
                    sortOrder: Number.isFinite(so) ? so : 0,
                  });
                  setGroupOpen(false);
                  setGfName("");
                  setGfCode("");
                  setGfSort("0");
                  setGfParent("__root__");
                } catch {
                  /* */
                }
              }}
            >
              ບັນທຶກ
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={diagramOpen}
        onOpenChange={(o) => {
          setDiagramOpen(o);
          if (!o) {
            setDfTitle("");
            setDfDiagramImageFile(null);
            setDfSort("0");
            setDiagramModalSaving(false);
          }
        }}
        title="ເພີ່ມແຜນຜັງ"
        size="md"
      >
        <div className="grid gap-2">
          <Input
            placeholder="ຫົວຂໍ້ (ທາງເລືອກ)"
            value={dfTitle}
            onChange={(e) => setDfTitle(e.target.value)}
          />
          <ImageKeyUploadField
            value=""
            className="w-full min-w-0"
            deferUpload
            pendingFile={dfDiagramImageFile}
            keyPrefix={EPC_DIAGRAM_IMAGE_KEY_PREFIX}
            label="ຮູບແຜນຜັງ *"
            aspectHint="ເລືອກຮູບແຜນ — ສະແດງເຕັມພື້ນກວ້າງ (contain); ອັບໂຫຼດເມື່ອກົດບັນທຶກ"
            aspectRatio="aspect-video"
            previewFit="contain"
            disabled={diagramModalSaving || createDiagram.isPending}
            onChange={() => {}}
            onFileSelect={(file) => setDfDiagramImageFile(file)}
            onClear={() => setDfDiagramImageFile(null)}
          />
          <Input
            placeholder="ລໍາດັບ"
            value={dfSort}
            onChange={(e) => setDfSort(e.target.value)}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={diagramModalSaving || createDiagram.isPending}
              onClick={() => setDiagramOpen(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="button"
              disabled={
                !selectedGroupId ||
                createDiagram.isPending ||
                diagramModalSaving
              }
              isLoading={diagramModalSaving || createDiagram.isPending}
              onClick={async () => {
                if (!(dfDiagramImageFile instanceof File)) {
                  toast.error("ເລືອກຮູບແຜນຜັງ");
                  return;
                }
                const so = Number.parseInt(dfSort, 10);
                setDiagramModalSaving(true);
                try {
                  const imageKey = await uploadImageToKey(
                    EPC_DIAGRAM_IMAGE_KEY_PREFIX,
                    dfDiagramImageFile,
                  );
                  await createDiagram.mutateAsync({
                    epcGroupId: selectedGroupId,
                    title: dfTitle.trim() ? dfTitle.trim() : null,
                    imageUrl: imageKey,
                    sortOrder: Number.isFinite(so) ? so : undefined,
                  });
                  setDiagramOpen(false);
                } catch {
                  /* toast ໂດຍ fetcher */
                } finally {
                  setDiagramModalSaving(false);
                }
              }}
            >
              ບັນທຶກ
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={chassisEditOpen}
        onOpenChange={(o) => {
          setChassisEditOpen(o);
          if (!o) resetChassisEditForm();
        }}
        title="ແກ້ໄຂຊ່ວງເລກຖັງ"
        size="md"
      >
        <div className="grid gap-2">
          <Input
            placeholder="ຄຳນຳໜ້າ VIN"
            value={ecfVin}
            onChange={(e) => setEcfVin(e.target.value)}
          />
          <Input
            placeholder="ຄຳນຳໜ້າເລກຖັງ"
            value={ecfChPre}
            onChange={(e) => setEcfChPre(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="ເລກຖັງ ຈາກ"
              value={ecfFrom}
              onChange={(e) => setEcfFrom(e.target.value)}
            />
            <Input
              placeholder="ເລກຖັງ ຫາ"
              value={ecfTo}
              onChange={(e) => setEcfTo(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <span className="block text-muted-foreground text-xs">
                ຜະລິດ ຕັ້ງແຕ່
              </span>
              <Input
                type="date"
                value={ecfProdF}
                onChange={(e) => setEcfProdF(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <span className="block text-muted-foreground text-xs">
                ຜະລິດ ຫາວັນທີ່
              </span>
              <Input
                type="date"
                value={ecfProdT}
                onChange={(e) => setEcfProdT(e.target.value)}
              />
            </div>
          </div>
          <Input
            placeholder="ໝາຍເຫດ"
            value={ecfNotes}
            onChange={(e) => setEcfNotes(e.target.value)}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setChassisEditOpen(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="button"
              disabled={!ecfId || updateChassis.isPending}
              onClick={async () => {
                if (!ecfId) return;
                try {
                  await updateChassis.mutateAsync({
                    id: ecfId,
                    input: {
                      vinPrefix: ecfVin.trim() ? ecfVin.trim() : null,
                      chassisPrefix: ecfChPre.trim() ? ecfChPre.trim() : null,
                      chassisFrom: ecfFrom.trim() ? ecfFrom.trim() : null,
                      chassisTo: ecfTo.trim() ? ecfTo.trim() : null,
                      productionFrom: ecfProdF.trim()
                        ? ecfProdF.trim()
                        : undefined,
                      productionTo: ecfProdT.trim()
                        ? ecfProdT.trim()
                        : undefined,
                      notes: ecfNotes.trim() ? ecfNotes.trim() : null,
                    },
                  });
                  setChassisEditOpen(false);
                  resetChassisEditForm();
                } catch {
                  /* toast ໂດຍ fetcher */
                }
              }}
            >
              ບັນທຶກ
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={groupEditOpen}
        onOpenChange={(o) => {
          setGroupEditOpen(o);
          if (!o) resetGroupEditForm();
        }}
        title="ແກ້ໄຂກຸ່ມ EPC"
        size="md"
      >
        <div className="grid gap-2">
          <Input
            placeholder="ຊື່ກຸ່ມ *"
            value={egfName}
            onChange={(e) => setEgfName(e.target.value)}
          />
          <Input
            placeholder="ລະຫັດກຸ່ມ"
            value={egfCode}
            onChange={(e) => setEgfCode(e.target.value)}
          />
          <Input
            placeholder="ລໍາດັບ (ຕົວເລກ)"
            value={egfSort}
            onChange={(e) => setEgfSort(e.target.value)}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setGroupEditOpen(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="button"
              disabled={!egfId || updateGroup.isPending}
              onClick={async () => {
                if (!egfId) return;
                const nameTrim = egfName.trim();
                if (!nameTrim) {
                  toast.error("ຕ້ອງມີຊື່ກຸ່ມ");
                  return;
                }
                const so = Number.parseInt(egfSort, 10);
                try {
                  await updateGroup.mutateAsync({
                    id: egfId,
                    input: {
                      groupName: nameTrim,
                      groupCode: egfCode.trim() ? egfCode.trim() : null,
                      sortOrder: Number.isFinite(so) ? so : undefined,
                    },
                  });
                  setGroupEditOpen(false);
                  resetGroupEditForm();
                } catch {
                  /* */
                }
              }}
            >
              ບັນທຶກ
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={diagramRenameOpen}
        onOpenChange={(o) => {
          setDiagramRenameOpen(o);
          if (!o) resetDiagramRenameForm();
        }}
        title="ແກ້ຊື່ແຜນ"
        size="sm"
      >
        <div className="grid gap-2">
          <Input
            placeholder="ຫົວຂໍ້ (ປ່ອຍວ່າງ = ບໍ່ຄ່າ)"
            value={dfRenameTitle}
            onChange={(e) => setDfRenameTitle(e.target.value)}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDiagramRenameOpen(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="button"
              disabled={!diagramRenameId || renameDiagram.isPending}
              onClick={async () => {
                if (!diagramRenameId) return;
                try {
                  await renameDiagram.mutateAsync({
                    id: diagramRenameId,
                    input: {
                      title: dfRenameTitle.trim() ? dfRenameTitle.trim() : null,
                    },
                  });
                  setDiagramRenameOpen(false);
                  resetDiagramRenameForm();
                } catch {
                  /* */
                }
              }}
            >
              ບັນທຶກ
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
